import { Box } from '@iheartjane/reefer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import type { BannerCarouselProps } from './bannerCarousel.types';
import { CarouselDots } from './carouselDots/carouselDots';
import { CarouselItem } from './carouselItem/carouselItem';
import { CarouselPlayPause } from './carouselPlayPause/carouselPlayPause';

import styles from './bannerCarousel.module.css';

const DEFAULT_AUTO_ADVANCE_MS = 7000;

/** How long a slide must stay active before it counts as viewed */
export const VIEW_DEBOUNCE_MS = 300;

/** How long a commanded slide keeps gating active reports before it expires */
export const TARGET_SETTLE_MS = 1000;

/** How much of the carousel must be on screen before a slide counts as viewed */
const IN_VIEW_THRESHOLD = 0.5;

/** Wraps past the last slide back to the first one */
const nextIndex = (current: number, total: number) => (current + 1) % total;

/** Wraps before the first slide back to the last one */
const previousIndex = (current: number, total: number) =>
  (current - 1 + total) % total;

export const BannerCarousel = ({
  ariaLabel,
  autoAdvanceMs = DEFAULT_AUTO_ADVANCE_MS,
  className,
  comparisonUrl,
  disableAutoAdvance = false,
  disableKeyboardNavigation = false,
  slides,
  onView,
  ...marginProps
}: BannerCarouselProps) => {
  const totalItems = slides.length;
  const isLooping = totalItems > 1;
  const isAutoAdvancing = isLooping && !disableAutoAdvance;

  const [reportedIndex, setReportedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const { ref: viewportRef, inView: isInView } = useInView({
    threshold: IN_VIEW_THRESHOLD,
  });

  // slides can disappear while active — never index past the end
  const activeIndex = Math.min(reportedIndex, totalItems - 1);

  const lastViewedIndexRef = useRef<number | null>(null);
  const targetIndexRef = useRef<number | null>(null);
  const targetTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const trackRef = useRef<HTMLDivElement>(null);

  const handleTogglePlayPause = () => setIsPaused((prev) => !prev);

  const handleSlideActive = useCallback((index: number) => {
    if (targetIndexRef.current != null && index !== targetIndexRef.current) {
      return;
    }

    targetIndexRef.current = null;
    setReportedIndex(index);
  }, []);

  const scrollToSlide = useCallback((index: number) => {
    const track = trackRef.current;
    const slide = track?.children.item(index) as HTMLElement | null;
    if (!track || !slide) return;

    targetIndexRef.current = index;
    clearTimeout(targetTimeoutRef.current);
    targetTimeoutRef.current = setTimeout(() => {
      targetIndexRef.current = null;
    }, TARGET_SETTLE_MS);

    track.scrollTo({ behavior: 'smooth', left: slide.offsetLeft });
  }, []);

  const goNext = useCallback(() => {
    if (!isLooping) return;

    scrollToSlide(nextIndex(activeIndex, totalItems));
  }, [activeIndex, isLooping, scrollToSlide, totalItems]);

  const goPrev = useCallback(() => {
    if (!isLooping) return;

    scrollToSlide(previousIndex(activeIndex, totalItems));
  }, [activeIndex, isLooping, scrollToSlide, totalItems]);

  useEffect(() => {
    if (disableAutoAdvance || !isLooping || isPaused) return;

    const interval = setInterval(() => {
      scrollToSlide(nextIndex(activeIndex, totalItems));
    }, autoAdvanceMs);

    return () => clearInterval(interval);
  }, [
    activeIndex,
    autoAdvanceMs,
    disableAutoAdvance,
    isLooping,
    isPaused,
    scrollToSlide,
    totalItems,
  ]);

  useEffect(() => {
    if (disableKeyboardNavigation) return;

    const arrowKeyHandlers = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };

    window.addEventListener('keyup', arrowKeyHandlers);

    return () => {
      window.removeEventListener('keyup', arrowKeyHandlers);
    };
  }, [disableKeyboardNavigation, goNext, goPrev]);

  // a changed slide list can invalidate the commanded target
  useEffect(() => {
    targetIndexRef.current = null;
  }, [totalItems]);

  useEffect(() => {
    if (!isInView) return;

    const timeout = setTimeout(() => {
      if (lastViewedIndexRef.current === activeIndex) return;
      lastViewedIndexRef.current = activeIndex;

      const slide = slides[activeIndex];
      onView?.(slide);

      // the ad can't see itself on screen, so its `viewed_ad` event is sent
      // from here; see `WebCustomImageAdEvent`
      if (slide?.kind === 'customImageAd') {
        slide.onEvent?.({ type: 'viewed_ad' });
      }
    }, VIEW_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [activeIndex, isInView, onView, slides, totalItems]);

  const handleCarouselItemClick = (index: number) => () => {
    scrollToSlide(index);
  };

  return (
    <Box
      ariaLabel={ariaLabel}
      className={className}
      data-testid="banner-carousel"
      {...marginProps}
    >
      <Box
        ref={viewportRef}
        className={styles['carouselViewport']}
        data-testid="carousel-viewport"
      >
        <div ref={trackRef} className={styles['carouselTrack']}>
          {slides.map((slide, index) => (
            <CarouselItem
              key={slide.id}
              activeIndex={activeIndex}
              comparisonUrl={comparisonUrl}
              index={index}
              onActive={handleSlideActive}
              onClick={handleCarouselItemClick(index)}
              slide={slide}
            />
          ))}
        </div>

        {isAutoAdvancing && (
          <CarouselPlayPause
            isPaused={isPaused}
            onToggle={handleTogglePlayPause}
          />
        )}
      </Box>

      <CarouselDots
        activeIndex={activeIndex}
        autoAdvanceMs={disableAutoAdvance ? undefined : autoAdvanceMs}
        count={totalItems}
        isPaused={isPaused}
        onSelect={scrollToSlide}
      />
    </Box>
  );
};
